import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileStatus } from '../../../../../core/models/file-browser.models';

export interface ConflictEntry {
  filename: string;
  status: FileStatus | undefined;
}

@Component({
  selector: 'app-version-conflict-dialog',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-4 p-6">

        <div class="flex items-start gap-3 mb-4">
          <div class="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <svg class="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ conflicts.length === 1 ? 'File already exists' : conflicts.length + ' files already exist' }}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Uploading will create a new version and replace the current content.
            </p>
          </div>
        </div>

        <ul class="space-y-2 mb-5 max-h-48 overflow-y-auto">
          @for (entry of conflicts; track entry.filename) {
            <li class="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
              <span class="text-gray-800 dark:text-gray-200 truncate" [title]="entry.filename">{{ entry.filename }}</span>
              @switch (entry.status) {
                @case ('INGESTED') {
                  <span class="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                    Ingested
                  </span>
                }
                @case ('PARSED') {
                  <span class="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">Parsed</span>
                }
                @case ('UPLOADED') {
                  <span class="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Uploaded</span>
                }
              }
            </li>
          }
        </ul>

        @if (hasIngested) {
          <p class="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 mb-4">
            One or more files are already ingested into the Knowledge Graph. Uploading a new version will replace what the AI uses to answer questions.
          </p>
        }

        <div class="flex justify-end gap-2">
          <button
            (click)="cancel.emit()"
            class="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            Cancel
          </button>
          <button
            (click)="replace.emit()"
            class="px-4 py-2 text-sm rounded-lg font-medium transition-colors"
            [class]="hasIngested
              ? 'bg-amber-600 hover:bg-amber-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'">
            Replace {{ conflicts.length === 1 ? 'file' : 'files' }}
          </button>
        </div>
      </div>
    </div>
  `
})
export class VersionConflictDialogComponent {
  @Input() conflicts: ConflictEntry[] = [];
  @Output() replace = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  get hasIngested(): boolean {
    return this.conflicts.some(c => c.status === 'INGESTED');
  }
}
